import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * Step 1 of the SPA mass renewal: the supplier's quote workbooks → one
 * normalised table of priced quote lines, plus the old → new SPA
 * cross-reference when the workbook carries one.
 *
 * Replaces the Alteryx "prepare the individual quotes" region: the sheet
 * macro, the quote-number cleanup, the expiry filter, the per-each cost and
 * the keep-the-lower-price pass.
 */
export const spaRenewalReadQuotesStep = defineStepConfig({
  type: "spa_renewal_read_quotes",
  name: "SPA Renewal: Read Quotes",
  description:
    "Reads Hubbell Connect price list workbooks (C351_00_84_PRICE_<account>.xlsx) into one table of " +
    "priced lines with a cost per each, dropping expired and unpriced lines, and extracts the old → new SPA " +
    "cross-reference. Wire the files from a form trigger or any document-producing step.",
  category: "Transformation",
  libraryModule: "Pricing",

  inputSchema: z.object({
    quoteDocuments: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .min(1)
      .describe(
        "The supplier quote workbooks (.xlsx). Several files are read as one batch.",
      ),
  }),

  outputSchema: z.object({
    quoteLinesDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "CSV of every live quote line: SPA, type, dates, product, description, price, per, cost per each.",
    ),
    crossReferenceDocument: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "CSV of old SPA → new SPA pairs. Empty for formats where the SPA keeps its number.",
    ),
    quoteLineCount: z.number().int(),
    spaCount: z.number().int(),
    spaNumbers: z
      .array(z.string())
      .describe("The SPA numbers that have at least one live line."),
  }),

  configSchema: z.object({
    asOfDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional()
      .describe(
        "Quote lines whose validity ended before this date are dropped. Defaults to today.",
      ),
    includeExpiredQuotes: z.boolean().default(false),
    keepLowestCostWithinQuote: z
      .boolean()
      .default(true)
      .describe(
        "When a quote prices the same product twice, keep only the lower price.",
      ),
  }),

  emits: [] as const,
});
